import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Source} from "react-map-gl/mapbox";
import {useOperator} from "../../../context/OperatorContext";
import Spinner from "../../Spinner";
import Map, {VisitorDensityLayer} from "../../Map";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {useAuth} from "../../../context/AuthContext";
import {PlaybackSpeed, useVisitorDensityPlayback} from "./useVisitorDensityPlayback";
import {DateTime} from "luxon";
import {Interval, INTERVALS} from "../../../pages/Insights/View/VisitorDensity";
import {PlaybackControl} from "./PlaybackControl";

// Helpers ----

const playbackSpeedText: Record<PlaybackSpeed, string> = {
  500: '3x',
  1000: '2x',
  2000: '1x',
  3000: '0.5x',
}

// Types ----

type VisitorDensityTimelineMapProps = {
  height: string
  since?: DateTime
  until?: DateTime
  siteId?: number | null
  showSiteSelector?: boolean
  interval?: Interval
}

// Component ----

function VisitorsDensityTimelineMap({height, since, until, siteId, showSiteSelector, interval = INTERVALS['30_MINS']}: VisitorDensityTimelineMapProps) {
  const {settings} = useOperator();
  const {can} = useAuth();

  // Keep track of the selected site id
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);

  // Fetch site data
  const {data: site, isLoading: siteLoading} = useQuery(
    ['site', {id: selectedSiteId}],
    () => ngage.sites.get(Number(selectedSiteId)),
    {enabled: can('sites:read') && !!selectedSiteId}
  )

  // Convert date/time to the timezone of the selected site
  const sinceInTimezone = useMemo(() => site ? since?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, since]);
  const untilInTimezone = useMemo(() => site ? until?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, until]);

  const {
    currentInterval,
    startPlayback,
    stopPlayback,
    pausePlayback,
    skipBackward,
    skipForward,
    cyclePlaybackSpeed,
    playbackSpeed,
    isPlaying,
    isStart,
    isEnd
  } = useVisitorDensityPlayback({since: sinceInTimezone, until: untilInTimezone, interval});

  // Fetch activity heatmap data
  const {data: mapData, isLoading: mapDataLoading} = useQuery(
    ['maps.visitorDensity', {since: sinceInTimezone, until: untilInTimezone, siteId: selectedSiteId}],
    ({signal}) => ngage.maps.activity({since: sinceInTimezone, until: untilInTimezone, siteId: selectedSiteId}, {signal}),
    {
      enabled: !!selectedSiteId && !!sinceInTimezone && !!untilInTimezone,
      refetchOnWindowFocus: false,
    }
  );

  // Update the selected site ID when the siteId prop changes
  useEffect(() => {
    if (!siteId) return;
    setSelectedSiteId(siteId);
  }, [siteId]);

  const handleSiteSelect = useCallback((siteId: number | null) => {
    setSelectedSiteId(siteId)
  }, []);

  return (
    <div className='flex flex-col gap-6'>
      <Map
        height={height}
        defaultMapStyle={settings?.audience_app.map_style ?? null}
        initialViewState={settings?.audience_app.map_state ?? null}
        hidePointsOfInterest
        showSiteSelector={showSiteSelector}
        onSelectSite={handleSiteSelect}
        siteId={selectedSiteId}
      >
        {/* Timeline controls */}
        <PlaybackControl
          isReady={!!mapData}
          text={(!!mapData && currentInterval) ? currentInterval?.label : 'Loading...'}
          playbackSpeedLabel={playbackSpeedText[playbackSpeed]}
          onStartPlayback={startPlayback}
          onPausePlayback={pausePlayback}
          onStopPlayback={stopPlayback}
          onSkipBackward={skipBackward}
          onSkipForward={skipForward}
          onRewind={stopPlayback}
          onCyclePlaybackSpeed={cyclePlaybackSpeed}
          isPlaying={isPlaying}
          isStart={isStart}
          isEnd={isEnd}
        />

        {/* Data Sources */}
        {!!mapData &&
          <Source id="visitors-density-source" type="geojson" data={mapData}>
            <VisitorDensityLayer id="visitors-density" filter={!!currentInterval ? [
              "all",
              [">=", ["get", "occurred_at"], currentInterval.start],
              ["<=", ["get", "occurred_at"], currentInterval.end]
            ] : ["==", "1", "2"]}
            />
          </Source>
        }

        {/* Loading Spinner */}
        {(mapDataLoading || siteLoading) &&
          <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center pointer-events-none">
            <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.MEDIUM}/>
          </div>
        }
      </Map>
    </div>
  );
}



// Export ----

export default VisitorsDensityTimelineMap;
